'use client';

import {useForwardingRef} from '@rocket-ui/react-tools';
import {cn} from '@rocket-ui/tools';
import React, {
	ForwardRefExoticComponent,
	PropsWithoutRef,
	RefAttributes,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {isDesktop} from 'react-device-detect';
import {NewIconButton} from '../Button';
import {AutocompletedInput} from './AutocompletedInput';
import {withPrefix, withSuffix} from './hocs';
import styles from './Input.module.css';
import {InputDynamicPlaceholderFunc, InputPlaceholderFunc, InputProps} from './types';
import {FloatingAutocompletedInput} from './v2';

const InternalInput = React.forwardRef<HTMLInputElement, InputProps>(function InternalInput(
	{
		value,
		style,
		styles: stylesAttr,
		className,
		classes,
		maxLength,
		placeholder: inputPlaceholder,
		dynamicPlaceholder: inputDynamicPlaceholder,
		onFocus,
		onBlur,
		onClear,
		onChange,
		autoWidth,
		autoFocus,
		disableMetrics,
		name,
		...props
	},
	inputRef,
) {
	const ref = useForwardingRef(inputRef);
	const [focused, setFocused] = useState(false);

	const changeWidth = useCallback(() => {
		if (!autoWidth || !ref.current) return;

		let element = ref.current.nextElementSibling as HTMLInputElement;
		!element && (element = ref.current);

		ref.current.style.width = '1px';
		element.style.width = '1px';

		element.style.width = element.scrollWidth + 'px';
		ref.current.style.width = element.style.width;
	}, [autoWidth, ref]);

	useEffect(() => {
		changeWidth();
	}, [changeWidth]);

	const handleClickOnRoot = useCallback(() => {
		ref.current?.click();
		ref.current?.focus();
	}, [ref]);

	const handleClear = useCallback(
		async (event: React.MouseEvent) => {
			event.stopPropagation();
			await onClear?.();
			changeWidth();
			ref.current?.focus();
		},
		[onClear, ref, changeWidth],
	);

	const handleChange = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			if (event.target.value.length <= Number(maxLength) || !maxLength) {
				await onChange?.(event);
				changeWidth();
			}
		},
		[onChange, changeWidth],
	);

	const handleFocus = useCallback(
		(event: React.FocusEvent<HTMLInputElement>) => {
			setFocused(true);
			onFocus?.(event);
		},
		[onFocus, setFocused],
	);

	const handleBlur = useCallback(
		(event: React.FocusEvent<HTMLInputElement>) => {
			setFocused(false);
			onBlur?.(event);
		},
		[onBlur, setFocused],
	);

	const getPlaceholder: InputPlaceholderFunc = useMemo(
		() =>
			typeof inputPlaceholder === 'function'
				? inputPlaceholder
				: ({value}) => (value === '' || value === undefined ? inputPlaceholder : undefined),
		[inputPlaceholder],
	);

	const getDynamicPlaceholder: InputDynamicPlaceholderFunc = useMemo(
		() =>
			typeof inputDynamicPlaceholder === 'function'
				? inputDynamicPlaceholder
				: ({value}) =>
						inputDynamicPlaceholder && `${value ?? ''}${inputDynamicPlaceholder.slice(String(value ?? '').length)}`,
		[inputDynamicPlaceholder],
	);

	const placeholder = getPlaceholder({value});
	const dynamicPlaceholder = getDynamicPlaceholder({value});

	const isVisibleClearButton = Boolean(onClear) && value !== '' && value !== undefined;

	const computedProps = useMemo(() => {
		const inferredInputMode =
			props.inputMode ?? (props.type === 'number' ? 'numeric' : props.type === 'tel' ? 'tel' : undefined);
		return {
			...props,
			enterKeyHint: props.enterKeyHint ?? 'done',
			autoCorrect: props.autoCorrect ?? 'off',
			autoCapitalize: props.autoCapitalize ?? 'off',
			inputMode: inferredInputMode,
		};
	}, [props]);

	return (
		<div
			className={cn('flex w-full p-0 m-0 items-center', className, classes?.root)}
			style={{...style, ...stylesAttr?.root}}
			onClick={handleClickOnRoot}
		>
			<div className={cn(styles.inputContainer, classes?.container)}>
				<input
					value={value}
					ref={ref}
					className={cn(
						styles.nativeInput,
						classes?.nativeInput,
						disableMetrics ? 'ym-disable-keys ym-hide-content' : '',
					)}
					style={stylesAttr?.nativeInput}
					onChange={handleChange}
					onFocus={handleFocus}
					onBlur={handleBlur}
					autoFocus={autoFocus && isDesktop}
					{...computedProps}
				/>
				{placeholder && !focused && (
					<input
						value={placeholder}
						style={stylesAttr?.nativeInput}
						className={cn(
							styles.nativeInput,
							classes?.nativeInput,
							styles.placeholder,
							disableMetrics ? 'ym-disable-keys ym-hide-content' : '',
						)}
						disabled
						aria-disabled={true}
					/>
				)}
				{dynamicPlaceholder && (
					<input
						value={dynamicPlaceholder}
						style={stylesAttr?.nativeInput}
						className={cn(
							styles.nativeInput,
							classes?.nativeInput,
							styles.placeholder,
							disableMetrics ? 'ym-disable-keys ym-hide-content' : '',
						)}
						disabled
						aria-disabled={true}
					/>
				)}
			</div>
			{isVisibleClearButton && (
				<NewIconButton icon={'cross-mini'} variant={'ghost'} size={'xs'} onClick={handleClear} />
			)}
		</div>
	);
});
const PrefixedInput = withPrefix(InternalInput);
const SuffixedInput = withSuffix(InternalInput);

interface ExtendedInputProps extends InputProps {
	prefix?: string;
	suffix?: string;
}

const Input = React.forwardRef<HTMLInputElement, ExtendedInputProps>(function Input({prefix, suffix, ...props}, ref) {
	if (prefix) {
		return <PrefixedInput ref={ref} prefix={prefix} {...props} />;
	}

	if (suffix) {
		return <SuffixedInput ref={ref} suffix={suffix} {...props} />;
	}

	return <InternalInput ref={ref} {...props} />;
});

export interface InputSubmodules {
	Autocompleted: typeof AutocompletedInput;
	FloatingAutocompletedInput: typeof FloatingAutocompletedInput;
}

type RefFC<T, P> = ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>>;

const InputNamespace: RefFC<HTMLInputElement, ExtendedInputProps> & InputSubmodules = Object.assign(Input, {
	Autocompleted: AutocompletedInput,
	FloatingAutocompletedInput: FloatingAutocompletedInput,
});

export {InputNamespace as Input};
